import { computed } from 'vue'
import { useCatalogsStore } from '@/stores/catalogs'
import { useCompanyStore } from '@/stores/company'
import { getStatusColorStyle, DEFAULT_STATUS_COLOR_CLASSES } from '@/utils/statusColor'
import { shipmentStatusConfig } from '@/infrastructure/config/company/shipment-status.config'
import { allStatusSetStatuses } from '@/utils/shipmentStatusSets'

export interface PurchaseOrderStatus {
  order: number
  trigger?: string
  triggerType?: 'field' | 'template' | 'manual' | 'creation'
  templateTrigger?: string
  type: string
  label: string
  id: string
  visible?: boolean
  isManual?: boolean
  description?: string
  color?: string
}

const normalizeStatus = (str: string) =>
  String(str).toUpperCase().trim().replace(/\s+/g, '_')

export const usePurchaseOrderStatus = () => {
  const catalogsStore = useCatalogsStore()
  const companyStore = useCompanyStore()

  /**
   * Carga la config desde Firestore si aún no está en el store.
   * Llamar en onMounted de páginas que usan estados de OC.
   */
  const initStatuses = async () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) return
    if ((catalogsStore.configs as any)?.PURCHASE_ORDER_STATUS) return
    await catalogsStore.loadConfigs(companyId)
  }

  /**
   * Estados por defecto - SOLO se usan si NO existe configuración específica del cliente.
   * Estos son los 3 estados básicos del sistema.
   */
  const DEFAULT_STATUSES: PurchaseOrderStatus[] = [
    { order: 0, id: 'TO_BE_CONFIRMED', label: 'To Be Confirmed', type: 'string', visible: true },
    { order: 1, id: 'APPROVED', label: 'Approved', type: 'string', visible: true },
    { order: 2, id: 'PARTIALLY_CONFIRMED', label: 'Partially Confirmed', type: 'string', visible: true },
  ]

  /**
   * Verifica si existe configuración específica del cliente.
   */
  const hasCustomConfig = computed((): boolean => {
    const statusConfig = (catalogsStore.configs as any)?.PURCHASE_ORDER_STATUS
    return !!(statusConfig?.status && Array.isArray(statusConfig.status) && statusConfig.status.length > 0)
  })

  /**
   * Todos los estados visibles ordenados.
   * Si existe configuración específica → usa SOLO esos estados.
   * Si NO existe configuración → usa estados por defecto.
   */
  const getAllStatuses = computed((): PurchaseOrderStatus[] => {
    const statusConfig = (catalogsStore.configs as any)?.PURCHASE_ORDER_STATUS
    
    // Si existe config específica, usar SOLO esos estados
    if (statusConfig?.status && Array.isArray(statusConfig.status) && statusConfig.status.length > 0) {
      return statusConfig.status
        .filter((s: PurchaseOrderStatus) => s.visible !== false)
        .sort((a: PurchaseOrderStatus, b: PurchaseOrderStatus) => a.order - b.order)
    }
    
    // Si NO existe config, usar estados por defecto
    return DEFAULT_STATUSES
  })

  const getStatusConfig = (statusId: string): PurchaseOrderStatus | undefined => {
    return getAllStatuses.value.find((s) => s.id === statusId)
  }

  const getStatuses = getAllStatuses

  const getStatusLabel = (statusId: string): string => {
    if (!statusId) return ''
    const statuses = getAllStatuses.value
    const normalizedSearchId = normalizeStatus(statusId)
    
    return (
      // Búsqueda exacta
      statuses.find((s) => s.id === statusId)?.label ||
      // Búsqueda normalizada (espacios y guiones bajos)
      statuses.find((s) => normalizeStatus(s.id) === normalizedSearchId)?.label ||
      // Búsqueda case-insensitive
      statuses.find((s) => s.id.toUpperCase() === statusId.toUpperCase())?.label ||
      // Búsqueda parcial
      statuses.find((s) =>
        normalizeStatus(s.id).includes(normalizedSearchId) || 
        normalizedSearchId.includes(normalizeStatus(s.id))
      )?.label ||
      // Si no encuentra nada, devolver el ID
      statusId
    )
  }

  const getStatusFilterOptions = computed(() => {
    const poStatuses = getAllStatuses.value.map((s, idx) => ({
      id: s.id,
      label: s.label,
      color: s.color,
      _group: 0,
      _order: s.order ?? idx,
    }))

    const shipmentConfig = (catalogsStore.configs as any)?.SHIPMENT_STATUS
    const shipmentSource = shipmentConfig || shipmentStatusConfig

    const shipmentBuckets: any[] = []
    if (Array.isArray(shipmentSource.status)) shipmentBuckets.push(...shipmentSource.status)
    if (shipmentSource.statusByMode && typeof shipmentSource.statusByMode === 'object') {
      for (const mode of Object.keys(shipmentSource.statusByMode)) {
        const list = shipmentSource.statusByMode[mode]
        if (Array.isArray(list)) shipmentBuckets.push(...list)
      }
    }
    shipmentBuckets.push(...allStatusSetStatuses(shipmentSource))

    const poIds = new Set(poStatuses.map((s) => normalizeStatus(s.id)))
    const seenShipment = new Set<string>()
    const shipmentStatuses: typeof poStatuses = []
    for (const s of shipmentBuckets) {
      if (!s?.id || s.visible === false) continue
      const norm = normalizeStatus(s.id)
      if (poIds.has(norm) || seenShipment.has(norm)) continue
      seenShipment.add(norm)
      shipmentStatuses.push({ id: s.id, label: s.label, color: s.color, _group: 1, _order: s.order ?? 0 })
    }

    const all = [...poStatuses, ...shipmentStatuses]
    all.sort((a, b) => a._group !== b._group ? a._group - b._group : a._order - b._order)

    return all.map(({ id, label, color }) => ({ id, label, color }))
  })

  const getStatusColorValue = (statusId: string): string | undefined => {
    return getStatusConfig(statusId)?.color
  }

  const getStatusClasses = (statusId: string): string => {
    const configured = getStatusColorValue(statusId)
    if (configured) {
      const resolved = getStatusColorStyle(configured)
      if (resolved.mode === 'classes') return resolved.classes
      return DEFAULT_STATUS_COLOR_CLASSES
    }

    const classMap: Record<string, string> = {
      'TO_BE_CONFIRMED': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      'TO_BE_APPROVED': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'CONFIRMED': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'APPROVED': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'PENDING': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'IN_TRANSIT': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'DELIVERED': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      'COMPLETED': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'CANCELLED': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    }
    return classMap[statusId] || DEFAULT_STATUS_COLOR_CLASSES
  }

  const isLoaded = computed(() => !!(catalogsStore.configs as any)?.PURCHASE_ORDER_STATUS)

  return {
    initStatuses,
    getAllStatuses,
    getStatuses,
    getStatusConfig,
    getStatusLabel,
    getStatusFilterOptions,
    getStatusClasses,
    getStatusColorValue,
    isLoaded,
    hasCustomConfig,
  }
}
